export default ({ $axios, app, $config, store }, inject) => {
  inject('helper', {
    name: 'helper',
    formatPrice(price) {
      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
      }).format(price);
    },
    metaTitle(title) {
      if (title) {
        return title;
      }
      return store.getters['settings/businessVariables'].seoDefault.title;
    },
    schemaData(data) {
      if (!data) return '';
      const isArray = Array.isArray(data);

      if (isArray) {
        const scripts = [];
        data.forEach(script => {
          scripts.push({ type: 'application/ld+json', json: script });
        });

        return scripts;
      } else {
        return [{ type: 'application/ld+json', json: data }];
      }
    },
    metaDescription(array) {
      const defaultMeta = [...store.getters['settings/businessVariables'].seoDefault.metaTags];
      if (!array) {
        return defaultMeta;
      }

      const newMeta = [];

      array.forEach(el => {
        if (el.name) {
          el.hid = el.name;
        }

        if (el.content) {
          newMeta.push(el);
        }
      });

      return newMeta.reduce(
        (res, item) => res.filter(i => i.name !== item.name).concat(item),
        defaultMeta,
      );
    },
    countPaginationLimit(limit) {
      return !store.getters['auth/subscribedToNewsletter'] ? limit - 1 : limit;
    },
    countPaginationOffset(limit, offset) {
      return !store.getters['auth/subscribedToNewsletter'] ? offset - offset / limit : offset;
    },
    countCurrentPage(limit, offset) {
      const newLimit = this.countPaginationLimit(limit);
      const newOffset = this.countPaginationOffset(limit, offset);

      return newOffset / newLimit + 1;
    },
    prevNextUrl(query, offset, limit, total, baseUrl, pageUrl) {
      const currentPage = this.countCurrentPage(limit, offset);
      const newLimit = this.countPaginationLimit(limit);
      let nextLink = {};
      let prevLink = {};
      let pageCount = Math.ceil(total / newLimit);
      if (pageCount === 0) {
        pageCount = 1;
      }

      if (currentPage > 2) {
        prevLink = {
          name: 'prev',
          content: `${$config.baseUrl}${app.localePath({
            name: pageUrl,
            params: { number: currentPage - 1 },
            query,
          })}`,
        };
      } else if (currentPage === 2 && currentPage > 1) {
        prevLink = {
          name: 'prev',
          content: `${$config.baseUrl}${app.localePath({
            name: baseUrl,
            query,
          })}`,
        };
      }

      if (currentPage < pageCount) {
        nextLink = {
          name: 'next',
          content: `${$config.baseUrl}${app.localePath({
            name: 'search-page-number',
            params: { number: currentPage + 1 },
            query,
          })}`,
        };
      }

      return [prevLink, nextLink];
    },
    optimizeImageUrl(src, params) {
      if (!src) return '';

      if (src.includes('.svg')) {
        return src;
      }

      const supportAvif = store.getters['settings/supportAvif'];
      const baseUrl = $config?.baseOfixUrl;

      if (!baseUrl) {
        return src;
      }

      const newSrc = src.substring(baseUrl.length);
      const name = src.split('/').pop()?.split('.')[0] ?? '';

      return `${baseUrl}/e-commerce/v1/images/convert${newSrc}/${params}/${name}.${
        supportAvif ? 'avif' : 'webp'
      }`;
    },
    shortText(text, number) {
      if (text.length > number) {
        return `${text.substring(0, number)}...`;
      } else {
        return text;
      }
    },
    changeHref(string) {
      if (string.includes($config.baseOfixUrl)) {
        return string.substring($config.baseOfixUrl.length);
      } else {
        return string;
      }
    },
    loadInlineScript(src, id) {
      let shouldAppend = false;
      let el = document.querySelector(`#${id}`);
      if (!el) {
        el = document.createElement('script');
        el.async = true;
        el.defer = true;
        el.id = id;
        el.innerHTML = src;
        el.hid = id;
        shouldAppend = true;
      }

      if (shouldAppend) document.head.appendChild(el);
    },
    loadScript(src, id) {
      return new Promise((resolve, reject) => {
        let shouldAppend = false;
        let el = document.querySelector('script[src="' + src + '"]');
        if (!el) {
          el = document.createElement('script');
          el.type = 'text/javascript';
          el.async = true;
          el.defer = true;
          el.src = src;
          el.id = id;
          shouldAppend = true;
        } else if (el.hasAttribute('data-loaded')) {
          resolve(el);
          return;
        }

        el.addEventListener('error', reject);
        el.addEventListener('abort', reject);
        el.addEventListener('load', function loadScriptHandler() {
          el.setAttribute('data-loaded', true);
          resolve(el);
        });

        if (shouldAppend) document.head.appendChild(el);
      });
    },
    removeScriptById(id) {
      const el = document.getElementById(id);
      if (el && el.tagName === 'SCRIPT') {
        el.remove();
        return true; // Successfully removed
      }
      return false; // Not found or not a <script> element
    },
    kebabCase(str) {
      return str
        .replace(/([A-Z])([A-Z])/g, '$1-$2')
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/[\s_]+/g, '-')
        .replace('!', '')
        .replace('?', '')
        .replace('.', '')
        .replace(',', '')
        .replace(';', '')
        .replace(':', '')
        .replace('&nbsp', '')
        .replace(' ', '')
        .toLowerCase();
    },
    scrollToId(id) {
      const target = document.querySelector(`#${id}`);
      const header = document.querySelector('.pageHeader');
      window.scrollTo({
        top: target.offsetTop - (header.clientHeight + 15),
        left: 0,
        behavior: 'smooth',
      });
    },
    getPriceDetails(data) {
      const { net, gross, netFormatted, grossFormatted, currencyPostfix, currencyPrefix } = data;
      return { net, gross, netFormatted, grossFormatted, currencyPostfix, currencyPrefix };
    },
    prepareMultiPacks(productData) {
      if (typeof productData.multiPacks !== 'undefined') {
        let preparedOptions = [];

        if (Array.isArray(productData.multiPacks) && productData.multiPacks.length) {
          preparedOptions = [
            ...preparedOptions,
            ...productData.multiPacks
              .map(option => {
                if (typeof option.unitPrice !== 'undefined') {
                  const priceObject = this.getPriceDetails(option.priceObject);
                  const unitPrice = this.getPriceDetails(option.unitPrice);
                  return {
                    id: option.id,
                    name: option.name,
                    quantity: option.quantity,
                    isPallet: option.isPallet,
                    uom: option.uom ?? 'szt.',
                    slug: option.slug,
                    isCollectivePack: option.isCollectivePack,
                    priceObject,
                    unitPrice,
                    discount: option.discount,
                    mainPictureUrl: option.mainPictureUrl,
                  };
                }
                return false;
              })
              .filter(item => !!item),
          ];
        }
        return preparedOptions;
      }
      return [];
    },
    calculateDiscount(currentPrice, oldPrice) {
      const discount = 100 - (oldPrice * 100) / currentPrice;
      return Math.round(discount);
    },
  });
};
